import {WATCHLIST} from '/enum/request.enum';
import {request} from '/redux/api/request';
import {createPromisedAction} from '/util/action.util';

export const RESET_REPORT: string = 'RESET_REPORT';
export const RESET_REPORT_META: string = 'RESET_REPORT_META';
export const SET_STATE: string = 'REPORT/SET_STATE';
import type {PeriodFilter} from '/component/screen/report/Report.types';
import {VIEW_TEAMS_LOADING_STATE} from '/enum/myTeams.enum';
import {REPORT_WATCH_LIST, VIEW_TEAMS} from '/enum/records.enum';

import {getErrorHandler} from './apiError.action';

export interface MyTeamsFilters {
  rank?: string[];
  fibc?: string;
  period?: string;
  frontlineOnly?: boolean;
  fqvMin?: number;
  fqvMax?: number;
  newClientsAbove?: number;
  newClientsBelow?: number;
  qualifiedClientsAbove?: number;
  qualifiedClientsBelow?: number;
  newCoachesAbove?: number;
  newCoachesBelow?: number;
  cabEarned?: boolean;
  aabEarned?: boolean;
  closeToNextRank?: boolean;
}

/* ------------------------- MyTeams Actions ------------------------- */
export const GET_VIEW_TEAMS: string = 'MY_TEAMS/GET_VIEW_TEAMS';
export const RESET_MY_TEAMS_INFO: string = 'RESET_MY_TEAMS_INFO';
export const RESET_VIEW_TEAMS_INFO: string = 'RESET_VIEW_TEAMS_INFO';
export const UPDATE_MY_TEAMS_FILTER = 'UPDATE_MY_TEAMS_FILTER';

export const RESET_ALL_MY_TEAMS_INFO: string = 'RESET_ALL_MY_TEAMS_INFO';
export const MyteamsReduxKeys = [
  'MY_TEAMS-rankDesc-ASC-default--Filters-N/A',
  'MY_TEAMS-lastNameZA-DESC-default--Filters-N/A',
  'MY_TEAMS-rankDesc-DESC-default--Filters-N/A',
  'MY_TEAMS-activationDateNew-ASC-default--Filters-N/A',
  'MY_TEAMS-activationDateOld-DESC-default--Filters-N/A',
];

export const updateMyTeamsFilter = (filters: MyTeamsFilters) => ({
  type: UPDATE_MY_TEAMS_FILTER,
  payload: filters,
});

export const getViewTeams = createPromisedAction(
  GET_VIEW_TEAMS,
  ['coachId', 'pageNumber', 'appendData'],
  (dispatchMe: any, inputPayload: any, dispatch: any) => {
    const {
      coachId,
      pageNumber = 1,
      reportParams,
      appendData,
      isDrilldown,
    } = inputPayload;

    const rawParams = reportParams?.[0] ?? {};

    // Extract EVERYTHING from rawParams (single source of truth)
    const {
      rank,
      sortBy,
      sortOrder,
      fibc = 'any',
      frontline = '0',
      closeToNextRank,
      cabEarned = false,
      aabEarned = false,
      fqvMin,
      fqvMax,
      newClientsAbove,
      newClientsBelow,
      qualifiedClientsAbove,
      qualifiedClientsBelow,
      newCoachesAbove,
      newCoachesBelow,
      days,
      month,
    } = rawParams;

    // Build period from rawParams ONLY
    const period: PeriodFilter | undefined = days
      ? {type: 'days', value: days}
      : month
      ? {type: 'month', value: month}
      : undefined;

    return Promise.resolve()
      .then(() =>
        // Set loading true before API request
        dispatch(
          viewTeamsState({
            stateName: VIEW_TEAMS_LOADING_STATE,
            stateValue: true,
            reportName: VIEW_TEAMS,
          }),
        ),
      )
      .then(() => {
        const body: any = {
          id: coachId,
        };

        // Only add parameters if they differ from defaults
        if (pageNumber && pageNumber !== 1) {
          body.pageNumber = pageNumber;
        }
        if (rank) {
          body.rank = rank;
        }
        if (sortBy && sortBy !== 'rank') {
          body.sortBy = sortBy;
        }
        if (sortOrder && sortOrder !== 'DESC') {
          body.sortOrder = sortOrder;
        }
        if (fibc && fibc !== 'any') {
          body.fibc = fibc;
        }
        if (frontline && frontline !== '0') {
          body.frontline = frontline;
        }
        if (period) {
          if (period.type === 'days') {
            body.days = period.value;
          }
          if (period.type === 'month') {
            body.month = period.value;
          }
        }

        if (cabEarned) {
          body.cabEarned = cabEarned;
        }
        if (aabEarned) {
          body.aabEarned = aabEarned;
        }
        if (closeToNextRank) {
          body.closeToNextRank = closeToNextRank;
        }
        fqvMin !== undefined && (body.fqvMin = fqvMin);
        fqvMax !== undefined && (body.fqvMax = fqvMax);
        newClientsAbove !== undefined &&
          (body.newClientsAbove = newClientsAbove);
        newClientsBelow !== undefined &&
          (body.newClientsBelow = newClientsBelow);
        qualifiedClientsAbove !== undefined &&
          (body.qualifiedClientsAbove = qualifiedClientsAbove);
        qualifiedClientsBelow !== undefined &&
          (body.qualifiedClientsBelow = qualifiedClientsBelow);
        newCoachesAbove !== undefined &&
          (body.newCoachesAbove = newCoachesAbove);
        newCoachesBelow !== undefined &&
          (body.newCoachesBelow = newCoachesBelow);
        return request(VIEW_TEAMS, {
          body,
          getHeaders: ['report-count', 'report-count-allorg'],
        });
      })
      .then(({data: myTeams, responseHeaders}: any) => {
        const basePayload: any = {
          coachId,
          myTeams,
          filters: rawParams,
          sortParams: {sortBy, sortOrder},
          appendData,
        };
        // Only add reportFilterCount & reportCountAllOrg if NOT drilldown
        const reportFilterCount = Number(responseHeaders?.[0] ?? 0);
        const reportCountAllOrg = Number(responseHeaders?.[1] ?? 0);

        if (isDrilldown) {
          return {
            myTeams,
            reportFilterCount_DrillDown: reportFilterCount,
            reportCountAllOrg_DrillDown: reportCountAllOrg,
            appendData,
          };
        } else {
          dispatchMe(() => ({
            ...basePayload,
            reportFilterCount,
            reportCountAllOrg,
          }));

          return {
            myTeams,
            reportFilterCount,
            reportCountAllOrg,
            appendData,
          };
        }
      })
      .then(result => {
        // Set loading false after API finishes
        dispatch(
          viewTeamsState({
            stateName: VIEW_TEAMS_LOADING_STATE,
            stateValue: false,
            reportName: VIEW_TEAMS,
          }),
        );
        return result;
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.

        getErrorHandler(dispatch, {
          promisedAction: getViewTeams,
          params: inputPayload,
        }),
      );
  },
);

export const viewTeamsState = createPromisedAction(VIEW_TEAMS_LOADING_STATE, [
  'stateName',
  'stateValue',
  'reportName',
]);

/* ------------------------- Watchlist Actions ------------------------- */

export const ADD_COACH_TO_WATCHLIST = 'WATCHLIST/ADD_COACH_TO_WATCHLIST';
export const REMOVE_COACH_FROM_WATCHLIST =
  'WATCHLIST/REMOVE_COACH_FROM_WATCHLIST';
export const GET_COACH_WATCHLIST = 'WATCHLIST/GET_COACH_WATCHLIST';
export const GET_WATCHLIST_DETAILS = 'WATCHLIST/GET_WATCHLIST_DETAILS';

// --- Watchlist Actions ---
// addCoachToWatchlist
export const addCoachToWatchlist = createPromisedAction(
  ADD_COACH_TO_WATCHLIST,
  ['downlineCoachId'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {downlineCoachId} = inputPayload;
    return Promise.resolve()
      .then(() => {
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListLoading',
            stateValue: true,
          }),
        );
      })
      .then(() =>
        request(WATCHLIST, {
          method: 'POST',
          body: {downlineCoachId},
          headers: {'Content-Type': 'application/json'},
        }),
      )
      .then(async (response: any) => {
        // update state with immediate response
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListIds',
            stateValue: {
              count: response?.count,
              downlineCoachIds: response?.downlineCoachIds,
            },
          }),
        );

        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        await dispatch(getCoachWatchlist());
        dispatchMe(response);
        return response;
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.

        getErrorHandler(dispatch, {
          promisedAction: addCoachToWatchlist,
          params: inputPayload,
        }),
      )
      .finally(() => {
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListLoading',
            stateValue: false,
          }),
        );
      });
  },
);

export const removeCoachFromWatchlist = createPromisedAction(
  REMOVE_COACH_FROM_WATCHLIST,
  ['downlineCoachId'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {downlineCoachId} = inputPayload;

    return Promise.resolve()
      .then(() => {
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListLoading',
            stateValue: true,
          }),
        );
      })
      .then(() =>
        request(WATCHLIST, {
          method: 'PUT',
          body: {downlineCoachId},
          headers: {'Content-Type': 'application/json'},
        }),
      )
      .then(async (response: any) => {
        // update state with immediate response
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListIds',
            stateValue: {
              count: response?.count,
              downlineCoachIds: response?.downlineCoachIds,
            },
          }),
        );

        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        await dispatch(getCoachWatchlist());

        dispatchMe(response);
        return response;
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.

        getErrorHandler(dispatch, {
          promisedAction: removeCoachFromWatchlist,
          params: inputPayload,
        }),
      )
      .finally(() => {
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListLoading',
            stateValue: false,
          }),
        );
      });
  },
);

export const getCoachWatchlist = createPromisedAction(
  GET_COACH_WATCHLIST,
  ['medifastCustomerNumber'],
  (dispatchMe: any, inputPayload?: any, dispatch?: any) => {
    const {medifastCustomerNumber} = inputPayload || {};

    return Promise.resolve()
      .then(() => {
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListLoading',
            stateValue: true,
          }),
        );
      })
      .then(() =>
        request(WATCHLIST, {
          method: 'GET',
          headers: {medifastCustomerNumber},
        }),
      )
      .then((response: any) => {
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListIds',
            stateValue: {
              count: response?.count,
              downlineCoachIds: response?.downlineCoachIds,
            },
          }),
        );
        dispatch({type: GET_COACH_WATCHLIST, payload: response});
        return response;
      })
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.

        getErrorHandler(dispatch, {
          promisedAction: getCoachWatchlist,
          params: inputPayload,
        }),
      )
      .finally(() => {
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListLoading',
            stateValue: false,
          }),
        );
      });
  },
);

export const setState = createPromisedAction(SET_STATE, [
  'reportName',
  'reportParams',
  'stateName',
  'stateValue',
]);
export const resetMyTeamsInfo = createPromisedAction(RESET_MY_TEAMS_INFO, []);

export const resetViewTeamsInfo = createPromisedAction(
  RESET_VIEW_TEAMS_INFO,
  [],
);

export const resetAllMyTeamsInfo = createPromisedAction(
  RESET_ALL_MY_TEAMS_INFO,
  [],
);
export const resetReport = createPromisedAction(RESET_REPORT, [
  'reportKey',
  'data',
]);
export const resetReportMeta = createPromisedAction(RESET_REPORT_META, [
  'reportKey',
  'data',
]);

export const SET_SCREEN_CONTEXT = 'SET_SCREEN_CONTEXT';
export const setScreenContext = (screenContext: string) => ({
  type: SET_SCREEN_CONTEXT,
  payload: screenContext,
});

export const SET_IS_VIEW_TEAM = 'SET_IS_VIEW_TEAM';
export const setIsViewTeam = (isViewTeam: boolean | undefined) => ({
  type: SET_IS_VIEW_TEAM,
  payload: isViewTeam,
});

export const SET_SELECTED_TAB = 'SET_SELECTED_TAB';
export const setSelectedTab = (selectedTab: string) => ({
  type: SET_SELECTED_TAB,
  payload: selectedTab,
});
